/**
 * Page context derivation for the floating chat widget.
 * Maps the current URL pathname to a context object with page key,
 * prompt text for the collapsed widget bubble, and visibility flag.
 */

const PAGE_CONTEXTS = [
    {
        pattern: /^\/spotlights\/water/,
        page: 'water-spotlight',
        promptText: 'Ask about water data in Colorado',
    },
    {
        pattern: /^\/spotlights\/energy/,
        page: 'energy-spotlight',
        promptText: 'Ask about energy sustainability',
    },
    {
        pattern: /^\/spotlights\/wildfires/,
        page: 'wildfires-spotlight',
        promptText: 'Ask about wildfire data',
    },
    {
        pattern: /^\/spotlights\/data-centers-front-range/,
        page: 'data-centers-spotlight',
        promptText: 'Ask about data centers in Colorado',
    },
    {
        pattern: /^\/spotlights/,
        page: 'spotlights',
        promptText: 'Explore sustainability topics',
    },
    {
        pattern: /^\/data\/sources\//,
        page: 'data-source-detail',
        promptText: 'Ask about this data source',
    },
    {
        pattern: /^\/data\/products\//,
        page: 'data-product-detail',
        promptText: 'Ask about this data product',
    },
    {
        pattern: /^\/data\/organizations\//,
        page: 'org-detail',
        promptText: 'Ask about this organization',
    },
    {
        pattern: /^\/data/,
        page: 'data-discovery',
        promptText: 'Ask about this data',
    },
    {
        pattern: /^\/dashboard/,
        page: 'dashboard',
        promptText: '',
    },
    {
        pattern: /^\/advanced-query/,
        page: 'advanced-query',
        promptText: 'Need help with your query?',
    },
    {
        pattern: /^\/communities/,
        page: 'communities',
        promptText: 'Looking for a community?',
    },
];

// Pages where the widget should be hidden. Admin and moderator dashboards are
// focused moderation tools (their content scrolls inside a fixed-height panel),
// so the floating widget would overlap corner controls like the Approve/Reject
// buttons without adding value there.
const HIDDEN_PATTERNS = [
    /^\/bili-chat/,
    /^\/login/,
    /^\/signup/,
    /^\/forgot-password/,
    /^\/create-profile/,
    /^\/admin/,
    /^\/moderator/,
];

/**
 * Derive page context from the current pathname.
 * @param {string} pathname - Current URL pathname
 * @returns {{ page: string, promptText: string, visible: boolean }}
 */
export function getPageContext(pathname) {
    if (HIDDEN_PATTERNS.some((p) => p.test(pathname))) {
        return { page: null, promptText: '', visible: false };
    }

    const match = PAGE_CONTEXTS.find((ctx) => ctx.pattern.test(pathname));
    if (match) {
        return {
            page: match.page,
            promptText: match.promptText,
            visible: true,
        };
    }

    return {
        page: 'general',
        promptText: 'Ask about sustainability in Colorado',
        visible: true,
    };
}

/**
 * Get the top-level path segment for determining if navigation
 * should reset chat messages.
 * @param {string} pathname
 * @returns {string} The top-level path segment (e.g., "/spotlights", "/dashboard")
 */
export function getTopLevelPath(pathname) {
    const segments = pathname.split('/').filter(Boolean);
    return segments.length > 0 ? `/${segments[0]}` : '/';
}
